/* eslint-disable react/display-name */
import React, { forwardRef, ReactElement } from "react";
import { TransitionGroup, Transition } from "react-transition-group";
import classNames from "classnames";
import Button, { ButtonType } from "../Button/Button";
import Tooltip, { ITooltipProps } from "../Tooltip/Tooltip";
import { AnimationDuration } from "../../../styles/Animations";
import forceReflow from "../../../utils/forceReflow";

export interface IIconButtonProps extends React.ComponentProps<typeof Button> {
  /**
   * An Icon element e.g. <ArrowRight />
   */
  icon: ReactElement;
  /**
   * Props to pass to the Tooltip wrapping the button.
   * Leave as undefined for no Tooltip
   */
  tooltip?: ITooltipProps;
  /**
   * If the button is disabled, this will be used as the
   * tooltip's content instead
   */
  disabledReason?: string;
  /**
   * If no tooltip is provided, set an aria-label to
   * describe the button
   */
  "aria-label"?: string;
}

const IconButton = forwardRef<HTMLElement, IIconButtonProps>(
  ({ type, icon, disabled, tooltip, disabledReason, "aria-label": ariaLabel, ...buttonProps }, ref) => {
    const tooltipContent = disabled && !!disabledReason ? disabledReason : tooltip?.content;
    const tooltipBasedLabel = tooltip?.show ? undefined : tooltip?.content.toString();

    const renderButton = (): ReactElement => (
      <Button
        isIconOnly
        disabled={disabled}
        type={type}
        ref={ref}
        aria-label={ariaLabel || tooltipBasedLabel}
        {...buttonProps}
      >
        <TransitionGroup component={null}>
          <Transition onEnter={forceReflow} key={icon.key ?? undefined} timeout={AnimationDuration.Medium}>
            {(status): ReactElement => (
              <div className={classNames("button__icon-wrapper", `button__icon-wrapper--${status}`)}>{icon}</div>
            )}
          </Transition>
        </TransitionGroup>
      </Button>
    );

    if (tooltip) {
      return (
        /**
         * @NOTE(mbusby, 2020-10-07):
         * We apply wrapTarget when the button is disabled, as not all browsers
         * support mouse events on disabled buttons, which are required
         * for the `Tooltip` to work correctly.
         */
        <Tooltip wrapTarget={disabled} {...tooltip} content={tooltipContent ?? ""}>
          {renderButton()}
        </Tooltip>
      );
    }

    return renderButton();
  },
);

IconButton.defaultProps = {
  type: ButtonType.Tertiary,
};

export default IconButton;
